import React from 'react';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import 'swiper/css';

const ChatBubble = ({ message, sender, delay, className }) => {
  const isLeft = sender === 'pastor';
  
  return (
    <motion.div
      initial={{ opacity: 0, x: isLeft ? -50 : 50 }}
      whileInView={{ 
        opacity: 1, 
        x: 0,
        transition: { duration: 0.5, delay }
      }}
      viewport={{ 
        once: true,
        margin: "-100px" // Reduced margin for mobile
      }}
      className={`flex ${isLeft ? 'justify-start' : 'justify-end'} my-6 md:my-12`} // Reduced vertical margin on mobile
    >
      <div className={`max-w-md p-3 md:p-4 rounded-2xl text-base md:text-lg whitespace-pre-line ${
        isLeft 
          ? 'bg-blue-500 text-white rounded-tl-none' 
          : 'bg-gray-100 text-gray-800 rounded-tr-none'
      } ${className || ''}`}>
        <div
          dangerouslySetInnerHTML={{ __html: renderMarkdown(message) }}
        />
      </div>
    </motion.div>
  );
};

function renderMarkdown(text) {
  return text.replace(/\*\*(.+?)\*\*/g, '<i><strong>$1</strong></i>');
}

const MESSAGES = [
  { sender: 'pastor', message: "Hi! Welcome to our church! 👋", delay: 0 },
  { sender: 'visitor', message: "Thanks! It's my first time here!", delay: 0.5 },
  { sender: 'pastor', message: `Awesome! We'd love to help you get connected! \n\nJust fill out this connect form so I can add your **name**, **email**, and **phone number** to our church database.`, delay: 1 },  
  // { sender: 'pastor', message: `Awesome! \n\nFill out this connect form:`, delay: 1 },
  // { sender: 'pastor', message: `Name: \n\nEmail: \n\nPhone: \n\nHome Address:   `, delay: 1 },
  // { sender: 'visitor', message: "That would be great!", delay: 1.5 },
  // { sender: 'pastor', message: "Cool! I'm just going to need you to fill out this connect form so I can add your name, email, and phone number to our church database.", delay: 2 },
  { sender: 'visitor', message: "Oh... uhh 😕", delay: 2.5 },
  { sender: 'pastor', message: "I promise we won't spam you!", delay: 3 },
  { sender: 'visitor', message: "😥", delay: 3.5, className: "!text-3xl" }
];

const QUOTES = [
  "Am I signing up for endless follow-ups?",
  "Just another name in a database.",
  "Why am I filling out a paper form in 2025?",
  "Where do I turn this in? Do I need a pen?",
  "Is this just a ploy to get me on a mailing list?",
  "What if I don’t want to share my info?",
  "This feels more like data collection than connection.",
  "Who exactly will have access to my information?",
  "This feels like filling out a DMV form.",
  "Will anyone actually read this?",
  "What happens to my info after I write it down?",
  "Is this really the best way to connect?",
  "Real relationships don’t start with a form."
];

const STUDY_QUOTES = [
  { 
    text: "Two-Thirds of Gen Z favor text messaging over email", 
    author: "PR Newswire",
    logo: "https://media.licdn.com/dms/image/v2/D560BAQH_GsDxqcHegQ/company-logo_200_200/company-logo_200_200/0/1719839993875/pr_newswire_logo?e=2147483647&v=beta&t=-R3vcISyTpVFE2bYaxDZzXCb9bnnHGXlkGmiIfV0_Kw"
  },
  { 
    text: "Nearly twice the respondents say texting is preferred over email.", 
    author: "Leadferno",
    logo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTvOiqnlXYa9OS0QpS8n_-tW6SzQvrxFJwS7g&s"
  },
  { 
    text: "Over 70% of Gen Z and Millennials favor messaging for communication", 
    author: "Webex Research",
    logo: "https://imageio.forbes.com/specials-images/imageserve/60bfa7a1c81d3bd4859b077d/Cisco-Webex/960x0.png?format=png&width=960"
  },
  { 
    text: `68% of millennials admit to texting "a lot" on a daily basis`, 
    author: "Forbes",
    logo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQsCj05p4vyZ2N9M5ng1gQol10jzx78ePwMXQ&s"
  },
  { 
    text: "Messaging apps are especially popular among young adults.", 
    author: "Pew Research Center",
    logo: "https://avatars.githubusercontent.com/u/10436827?s=280&v=4"
  },
  { 
    text: "Studies show young people choose messaging apps over email", 
    author: "The Mercury News",
    logo: "https://playonshakespeare.org/wp-content/uploads/2023/08/Press-Mercury_News-logo_wide.jpg"
  }
];

const Problem = () => {
  return (
    <div id="problem" className="about section">
      <div className="container mx-auto justify-center items-center">
        <div className="text-center wow fadeIn" data-wow-duration="1s" data-wow-delay="0.5s">
          <div className="about-right-content">
            <div className="section-heading">
              <h1 className="problem-header-image text-3xl md:text-4xl lg:text-5xl px-4">
                Get rid of the<br />
                <em>awkward connect card</em>
              </h1>
              <div className="line-dec inline-block"></div>
              <br />
              <br />
              <h8 className="text-xl md:text-2xl">
                Nobody wants it <span>anymore</span>
              </h8>
            </div>
          </div>
        </div>
      </div>
      
      {/* Chat message simulation */}
      <div className="container mx-auto px-4 md:px-8 py-6 md:py-12">
        <div className="max-w-3xl mx-auto">
          {MESSAGES.map((msg, index) => (
            <ChatBubble
              key={index}
              message={msg.message}
              sender={msg.sender}
              delay={msg.delay}
              className={msg.className}
            />
          ))}
        </div>
      </div>

      {/* Connect cards section */}
      <div className="container mx-auto pt-6 md:pt-10">
        <div className="flex flex-wrap">
          <div className="w-full text-center wow fadeIn" data-wow-duration="1s" data-wow-delay="0.5s">
            <div className="about-right-content">
              <div className="section-heading px-4 md:px-10 pb-4 md:pb-6">
                <h4 className="problem-header-image text-xl md:text-2xl">Connect cards scream "Spam emails"</h4>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quotes list */}
      <div className="container mx-auto px-4 justify-center items-center flex flex-col">
        {QUOTES.map((quote, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ 
              opacity: 1, 
              y: 0,
              transition: { 
                duration: 0.5
              }
            }}
            viewport={{ once: true, margin: "-100px" }}
            className="my-3 md:my-4 p-3 md:p-4 rounded-xl"
          >
            <p className="text-lg md:text-2xl text-black italic">"{quote}"</p>
          </motion.div>
        ))}
      </div>

      {/* Game has changed section */}
      <div className="container mx-auto flex items-center justify-center py-16 md:py-32">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{
            opacity: 1,
            scale: 1,
            transition: { duration: 1.2, ease: "easeOut" }
          }}
          viewport={{ once: true, amount: 0.5,  margin: "-150px" }} 
          className="text-center px-4"
        >
          <motion.h4
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="!text-4xl md:!text-6xl lg:!text-7xl font-black tracking-tight mb-6 md:mb-10 primary-gradient-text"
          >
            THE GAME HAS 
            <br/>
            CHANGED
          </motion.h4>
          <motion.h5
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold"
          >
            Change with it
          </motion.h5>
        </motion.div>
      </div>

      {/* Mobile-friendly swiper */}
      <div className="relative w-full py-8 md:py-16">
        <Swiper
          modules={[Autoplay]}
          spaceBetween={20}
          breakpoints={{
            0: {
              slidesPerView: 1,
            },
            480: {
              slidesPerView: 1.5,
            },
            640: {
              slidesPerView: 2,
            },
            768: {
              slidesPerView: 2.5,
            },
            1024: {
              slidesPerView: 3,
            },
            1280: {
              slidesPerView: 4,
            },
          }}
          loop={true}
          speed={20000}
          autoplay={{
            delay: 0,
            disableOnInteraction: false,
          }}
          className="mySwiper"
        >
          {STUDY_QUOTES.map((quote, index) => (
            <SwiperSlide key={index}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                className="flex items-center gap-3 md:gap-6 mx-4"
              >
                <div className="relative w-12 h-12 md:w-16 md:h-16 rounded-full bg-white shadow-lg overflow-hidden flex-shrink-0">
                  <img 
                    src={quote.logo} 
                    alt={quote.author}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
                <div className="bg-white/90 backdrop-blur-sm rounded-xl p-3 md:p-4 mb-3 md:mb-5 shadow-lg">
                  <p className="text-base md:text-lg text-gray-800 font-medium mb-2">
                    "{quote.text}"
                  </p>
                  <p className="text-xs md:text-sm text-gray-500 italic">
                    - {quote.author}
                  </p>
                </div>
              </motion.div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Final call to action */}
      <div className="container mx-auto pt-10 md:pt-20">
        <div className="flex flex-wrap">
          <div className="w-full text-center wow fadeIn" data-wow-duration="1s" data-wow-delay="0.5s">
            <div className="about-right-content">
              <div className="section-heading px-4 md:px-10 pb-6 md:pb-12">
                <h4 className="problem-header-image text-lg md:text-2xl"><i>"Studies show young people choose messaging apps over email"</i><span className="text-base md:text-xl"> <br />- The Mercury News</span></h4>
                <br />
                <br />
                <h4 className="corner-box text-2xl md:text-3xl lg:text-4xl px-4 py-10 md:py-0">Your ministry deserves to be at the front lines of communication</h4>
              </div>
            </div>
          </div>
        </div>
      </div> 
    </div>
  );
};

export default Problem;